import type {
  BootStrapBindParams,
  FieldSuggestion,
  FieldSuggestionsBindParams,
  FieldSuggestionsReturnParams,
  PostRenderBindParams,
  PreRenderBindParams,
  Setting,
  ShouldRenderBindParams,
} from '../types';
import { getKeys } from '@morph-mapper/utils';
import { useGlobalPluginStore } from '../store';
import {
  availablePlugins,
  isRegisteredPlugin,
  Plugin,
  registry,
} from '../registry';
import { SchemaVariant } from '@morph-mapper/types';
import type { Result } from 'neverthrow';
import { usePluginCtx } from '../context';

const parseSave = (save: unknown): any => {
  if (typeof save !== 'string') return save;
  try {
    return JSON.parse(save);
  } catch {
    return undefined;
  }
};

/**
 * The plugin configuration saved for a template, from the host's getTemplate
 * result: its `plugins` field when set, otherwise the plugin state stored in
 * its save (saving has not written `plugins` since #623). Empty when the
 * lookup failed or nothing was saved.
 */
export const savedPluginConfig = (result: Result<any, unknown>): any[] => {
  if (!result.isOk()) return [];
  const template = result.value;
  if (Array.isArray(template?.plugins)) return template.plugins;

  const global = parseSave(template?.save)?.plugins?.global;
  if (!Array.isArray(global?.plugins)) return [];
  return global.plugins.map((key: string) => ({
    key,
    enabled: true,
    settings: global.settings?.[key] ?? [],
  }));
};

export const usePlugins = () => {
  const [
    getPlugins,
    addPlugin,
    clearPlugins,
    getPluginVersion,
    setBootstrapped,
    getBootstrapped,
    resetAllBootstrapped,
    setGlobalState,
    getGlobalState,
    setFile,
    getFiles,
    shouldSkipFile,
    setTextContent,
    setSettings,
  ] = useGlobalPluginStore((s) => [
    s.getPlugins,
    s.addPlugin,
    s.clearPlugins,
    s.getPluginVersion,
    s.setBootstrapped,
    s.getBootstrapped,
    s.resetAllBootstrapped,
    s.setGlobalState,
    s.getGlobalState,
    s.setFile,
    s.getFiles,
    s.getSkipFile,
    s.setTextContent,
    s.setSettings,
  ]);

  const { createSetTemplatePlugins, createGetTemplate } = usePluginCtx();

  const getTemplate = createGetTemplate();
  const setTemplatePlugins = createSetTemplatePlugins();

  // Create instances up front of all available plugins.
  const instances = availablePlugins.map((plugin) => {
    const useInternal = registry[plugin];
    if (!useInternal) {
      throw new Error(`Unknown plugin “${String(plugin)}”`);
    }
    return useInternal();
  });

  const getInstanceSafe = (plugin: Plugin) => {
    const idx = getKeys(registry).indexOf(plugin);
    if (idx < 0) {
      throw new Error(`Plugin “${plugin}” is not registered.`);
    }
    return instances[idx];
  };

  const getSettings = (plugin: Plugin) => {
    const arr = getGlobalState().settings[plugin] || [];
    return arr.reduce(
      (acc, s) => {
        acc[s.key] = s;
        return acc;
      },
      {} as Record<string, Setting>,
    );
  };

  const describePlugin = (
    plugin: Plugin,
    savedSettings: Setting[] | undefined,
    enabled: boolean,
  ) => {
    const instance = getInstanceSafe(plugin);
    return {
      key: plugin,
      name: instance.name !== 'unknown' ? instance.name : plugin,
      version: instance.version,
      description: instance.description,
      settings: savedSettings?.length
        ? savedSettings
        : (instance.settings || []).map((s: any) => ({
            ...s,
            value: s.default,
          })),
      enabled,
    };
  };

  return {
    /**
     * Public methods which are defined by a plugin implementation.
     */
    config: (plugins: Plugin[], domain: string, fileType: SchemaVariant) => {
      setGlobalState({
        ...getGlobalState(), // existing values
        domain,
        fileType,
      });
      for (const name of plugins) {
        addPlugin(
          name,
          getInstanceSafe(name).version,
          getInstanceSafe(name).settings || [],
        );
        setBootstrapped(name, false);
      }
    },
    setContext: (domain: string, fileType: SchemaVariant) => {
      setGlobalState({
        ...getGlobalState(),
        domain,
        fileType,
      });
    },
    shouldRender: ({ entry }: ShouldRenderBindParams) => {
      // TODO: Currently if any plugin returns true (or none false -> all undefined), the node is rendered.
      // We probably want a priority system with required TRUE/FALSE and optional TRUE/FALSE.
      // If there are conflicting required TRUE/FALSE values, we should throw an error.
      // If a required TRUE/FALSE value conflicts with an optional TRUE/FALSE value, we prefer the required value.
      const results = getPlugins().map((plugin) =>
        getInstanceSafe(plugin).shouldRender(entry, getSettings(plugin)),
      );

      if (results.length === 0) {
        return true;
      }
      if (results.some((result) => result === true)) {
        return true;
      }
      if (results.every((result) => result === undefined)) {
        return true;
      }

      return false;
    },
    postRender: ({ entry, template }: PostRenderBindParams) => {
      let result = template;
      getPlugins().forEach(
        (plugin) =>
          (result = getInstanceSafe(plugin).postRender(
            {
              entry,
              template: result,
            },
            getSettings(plugin),
          )),
      );
      return result;
    },
    preRender: async ({
      entries,
      recomputeDependencies,
      ...params
    }: PreRenderBindParams) => {
      let hasModified = false;
      const lastFile = getFiles();
      const file = params.file.name + '_' + params.file.size;
      setFile(file);
      if (file === lastFile && shouldSkipFile()) {
        return hasModified;
      }

      const plugins = getPlugins().filter(
        (plugin, index, self) => index === self.findIndex((p) => p === plugin),
      );

      const preRenderPromises = plugins.map(async (plugin) => {
        await getInstanceSafe(plugin).preRender(
          {
            entries,
            ...params,
          },
          getSettings(plugin),
        );
        hasModified = true;
      });

      await Promise.all(preRenderPromises);

      recomputeDependencies(entries);

      return hasModified;
    },
    bootstrap: async ({
      entries,
      recomputeDependencies,
      force,
      exclude = [],
      ...params
    }: BootStrapBindParams) => {
      let hasModified = false;
      setTextContent(params.textContent);

      if (force) {
        resetAllBootstrapped();
      }

      const plugins = getPlugins().filter(
        (plugin, index, self) =>
          index === self.findIndex((p) => p === plugin) &&
          !exclude.includes(plugin),
      );

      const bootstrapPromises = plugins.map(async (plugin) => {
        if (getBootstrapped(plugin)) return;

        await getInstanceSafe(plugin).bootstrap(
          {
            entries,
            ...params,
          },
          getSettings(plugin),
        );
        setBootstrapped(plugin, true);
        hasModified = true;
      });

      await Promise.all(bootstrapPromises);

      recomputeDependencies(entries);

      return hasModified;
    },
    fieldSuggestions: async ({
      entry,
      iterator = false,
      iteratorStrategy,
      renderInContext,
    }: FieldSuggestionsBindParams) => {
      const rawResults = await Promise.all(
        getPlugins().map(async (plugin) => {
          const result = await Promise.resolve(
            getInstanceSafe(plugin).fieldSuggestions(
              {
                entry,
                iteratorStrategy,
                renderInContext,
              },
              getSettings(plugin),
            ),
          );
          if (!result) return [];

          return result.map((suggestions) => ({
            ...suggestions,
            source: plugin.toString(),
          }));
        }),
      );

      const results = rawResults
        .flat()
        .filter((value): value is FieldSuggestion => value !== undefined)
        .filter((value) =>
          iterator === true ? value.iterator === true : value.iterator !== true,
        );

      if (results.length === 0) {
        return [];
      }
      // Remove duplicates from results
      const suggestions = results
        .filter((value): value is FieldSuggestion => value !== undefined)
        .filter(
          (value, index, self) =>
            index ===
            self.findIndex(
              (t) =>
                t.value === value.value &&
                t.type === value.type &&
                t.snippet === value.snippet,
            ),
        );

      const rawPosts = await Promise.all(
        getPlugins().map((plugin) =>
          Promise.resolve(
            getInstanceSafe(plugin).postFieldSuggestions(
              {
                entry,
                suggestions,
                iteratorStrategy,
              },
              getSettings(plugin),
            ),
          ),
        ),
      );

      const posts = rawPosts
        .filter(
          (value): value is FieldSuggestionsReturnParams => value !== undefined,
        )
        .flat();

      if (posts.length === 0) {
        return suggestions;
      }

      // Plugins run postFieldSuggestions in parallel against the same
      // pre-processing snapshot, so a plugin that adds brand-new suggestions
      // (e.g. split-handling) never passes through sort-suggestions' own
      // scoring. Do one final merge sort here, by whatever score each
      // contributing plugin already attached, so the combined list stays
      // ranked best-first regardless of which plugin produced each entry.
      const sortedPosts = [...posts].sort((a, b) => {
        const scoreA = typeof (a as any).score === 'number' ? (a as any).score : 0;
        const scoreB = typeof (b as any).score === 'number' ? (b as any).score : 0;
        return scoreB - scoreA;
      });

      return sortedPosts;
    },

    /**
     * Private methods which are used by the plugin framework to connect to the application.
     * These methods should be hidden from the plugin implementations.
     */
    // TODO: state type is available in callee but not extracted from the save state definition (remove any).
    hydrate: (plugins: any[]) => {
      clearPlugins();
      // Quick fix to allow using older templates
      // TODO?: Should we convert old templates to support plugins
      if (!plugins) return;

      for (const plugin of plugins) {
        // Templates can still list plugins that were removed since they were
        // saved (e.g. discardOnUpdate); those are skipped instead of failing.
        if (!isRegisteredPlugin(plugin.key)) continue;

        setSettings(plugin.key as Plugin, plugin.settings || []);

        if (!plugin.enabled) continue;

        addPlugin(plugin.key as Plugin, plugin.version, plugin.settings || []);
        setBootstrapped(plugin.key, false);
        const pluginStore = getInstanceSafe(plugin.key)._store;
        pluginStore.getState().setState((draft) => {
          ((draft.domain = getGlobalState().domain),
            (draft.fileType = getGlobalState().fileType));
        });
      }

      // TODO: ADD VERSION MISMATCHING
    },
    preConfig: async (templateId: string) => {
      const dbPlugins = savedPluginConfig(await getTemplate(templateId));

      const plugins = Object.values(Plugin).map((plugin) => {
        const saved = dbPlugins?.find((p: any) => p.key === plugin);
        return describePlugin(
          plugin,
          saved?.settings,
          saved ? (saved.enabled ?? true) : false,
        );
      });

      return { plugins };
    },
    /**
     * The plugin configuration as it is active in this session (enabled
     * plugins and their settings), in the same shape as preConfig.
     */
    currentConfig: () => {
      const enabled = new Set(getPlugins());
      const plugins = Object.values(Plugin).map((plugin) =>
        describePlugin(
          plugin,
          getGlobalState().settings[plugin],
          enabled.has(plugin),
        ),
      );
      return { plugins };
    },
    serialize: (id?: string) => {
      const registered = getPlugins().reduce(
        (prev, current) => ({
          ...prev,
          [current]: {
            // Set version based on what is stored in the plugin framework.
            version: getPluginVersion(current),
            slice: getInstanceSafe(current)._store.getState().slice,
          },
        }),
        {},
      );
      if (id) {
        const name = (plugin: Plugin) =>
          getInstanceSafe(plugin).name !== 'unknown'
            ? getInstanceSafe(plugin).name
            : plugin;
        const enabledPlugin = new Set(getPlugins());
        const data = Object.values(Plugin).map((plugin) => ({
          key: plugin,
          name: name(plugin),
          version: getInstanceSafe(plugin).version,
          description: getInstanceSafe(plugin).description,
          settings: getGlobalState().settings[plugin] || [],
          enabled: enabledPlugin.has(plugin),
        }));

        setTemplatePlugins(id, data);
      }
      // Exclude textContent: it's derived at runtime, not persisted state.
      const { textContent, ...persistedGlobalState } = getGlobalState();

      return {
        global: persistedGlobalState,
        registered,
      };
    },
  };
};
